import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../clients/supabaseClient';
import type { Order, OrderWithActiveDelivery } from '../types';
import {
    fetchTotalDeliveryCountFromDb,
    fetchOrdersWithActiveDeliveriesFromDb,
} from '../services/deliveryService';
import {
    fetchHomeOrderCountsFromDb,
    fetchOrdersNeedingDriverFromDb,
} from '../services/orderService';
import { ORDER_STATUS } from '../constants/orderStatuses';

export type HomeData = {
    // KPI counts for the stat cards.
    openOrderCount:       number;
    activeDeliveryCount:  number;
    totalDeliveryCount:   number;
    awaitingPackingCount: number;
    packerAssignedCount:  number;
    // null = still loading; [] = loaded but empty.
    ordersNeedingDriver:        Order[] | null;
    ordersWithActiveDeliveries: OrderWithActiveDelivery[] | null;
    // Orders where a driver is assigned but has not yet accepted (DRIVER_ASSIGNED status).
    pendingAcceptance:          OrderWithActiveDelivery[] | null;
    // Call this to manually trigger a data refresh (e.g. after a mutation).
    refresh: () => Promise<void>;
};

export function useHomeData(): HomeData {

    // ── State ──────────────────────────────────────────────────────────────────

    const [openOrderCount,       setOpenOrderCount]       = useState(0);
    const [activeDeliveryCount,  setActiveDeliveryCount]  = useState(0);
    const [totalDeliveryCount,   setTotalDeliveryCount]   = useState(0);
    const [awaitingPackingCount, setAwaitingPackingCount] = useState(0);
    const [packerAssignedCount,  setPackerAssignedCount]  = useState(0);

    const [ordersNeedingDriver,        setOrdersNeedingDriver]        = useState<Order[] | null>(null);
    const [ordersWithActiveDeliveries, setOrdersWithActiveDeliveries] = useState<OrderWithActiveDelivery[] | null>(null);
    const [pendingAcceptance,          setPendingAcceptance]          = useState<OrderWithActiveDelivery[] | null>(null);

    // ── Refs ───────────────────────────────────────────────────────────────────

    // Concurrency guard: prevents overlapping fetches when realtime events
    // fire rapidly. If a refresh is already in flight, the incoming call sets
    // `pendingRefreshRef` and returns early. Once the in-flight refresh
    // completes it checks the flag and schedules one more refresh after a
    // short delay to pick up any changes that arrived mid-flight.
    const isRefreshingRef   = useRef(false);
    const pendingRefreshRef = useRef(false);

    // Debounce timer for the realtime subscription — prevents a burst of DB
    // events from triggering multiple back-to-back fetches.
    const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Stable ref to the latest `refresh` callback so the realtime effect
    // never captures a stale closure.
    const refreshRef = useRef<(() => Promise<void>) | null>(null);

    // ── Core fetch ─────────────────────────────────────────────────────────────

    const refresh = useCallback(async () => {
        if (isRefreshingRef.current) { pendingRefreshRef.current = true; return; }
        isRefreshingRef.current = true;
        try {
            // Bail out early if the session has expired to avoid 401s.
            const { data } = await supabase.auth.getSession();
            if (!data.session?.access_token) return;

            const [td, ond, owad, orderCounts] = await Promise.all([
                fetchTotalDeliveryCountFromDb(),
                fetchOrdersNeedingDriverFromDb(),
                fetchOrdersWithActiveDeliveriesFromDb(),
                fetchHomeOrderCountsFromDb(),
            ]);

            setOpenOrderCount(orderCounts.open);
            setAwaitingPackingCount(orderCounts.awaitingPacking);
            setPackerAssignedCount(orderCounts.packerAssigned);
            setTotalDeliveryCount(td);
            setOrdersNeedingDriver(ond);

            // Filter to ecom/chat orders only — other types don't go through
            // the driver delivery flow.
            const ecomOrChat = owad.filter(o => o.type === 'ecom' || o.type === 'chat');

            // Split into two groups:
            //   - pending: driver assigned but not yet accepted (DRIVER_ASSIGNED)
            //   - active:  driver on route (ON_ROUTE)
            const pending = ecomOrChat.filter(o => (o.delivery_status ?? '').trim().toUpperCase() === ORDER_STATUS.DRIVER_ASSIGNED);
            const active  = ecomOrChat.filter(o => (o.delivery_status ?? '').trim().toUpperCase() === ORDER_STATUS.ON_ROUTE);

            setPendingAcceptance(pending);
            setOrdersWithActiveDeliveries(active);
            setActiveDeliveryCount(active.length);
        } catch {
            // Silently fail — the realtime subscription will trigger a retry
            // on the next database change.
        } finally {
            isRefreshingRef.current = false;
            // Fire one more refresh if a request came in while this one was running.
            if (pendingRefreshRef.current) {
                pendingRefreshRef.current = false;
                window.setTimeout(() => { void refreshRef.current?.(); }, 1000);
            }
        }
    }, []);

    // ── Effects ────────────────────────────────────────────────────────────────

    // Keep the stable ref in sync with the latest callback.
    useEffect(() => { refreshRef.current = refresh; }, [refresh]);

    // Initial data load on mount.
    useEffect(() => { void refreshRef.current?.(); }, []);

    // Subscribe to realtime changes on `orders` and `deliveries`.
    // Uses a 750ms debounce and skips refreshes when the tab is hidden.
    useEffect(() => {
        const scheduleRefresh = () => {
            if (document.hidden) return;
            if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = setTimeout(() => { void refreshRef.current?.(); }, 750);
        };
        const channel = supabase
            .channel('home-realtime')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' },     scheduleRefresh)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'deliveries' }, scheduleRefresh)
            .subscribe();
        return () => {
            if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
            void supabase.removeChannel(channel);
        };
    }, []);

    return {
        openOrderCount,
        activeDeliveryCount,
        totalDeliveryCount,
        awaitingPackingCount,
        packerAssignedCount,
        ordersNeedingDriver,
        ordersWithActiveDeliveries,
        pendingAcceptance,
        refresh,
    };
}